import { Obj, load } from "scrivito"
import {
  ProductAttributes,
  ProductObj
} from "scrivito/Objs/Product/ProductObjClass"
import { ProductAddonAttributes } from "scrivito/Objs/ProductAddon/ProductAddonObjClass"
import { ProductAddonWrapperObj } from "scrivito/Objs/ProductAddonWrapper/ProductAddonWrapperObjClass"
import { ProductBaseVariantObj } from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { getCurrentSiteID } from "shared/lib/getCurrentSiteID"

import {
  getProductType,
  getVariantKeyAttributes
} from "entities/products/lib/getProductType"
import {
  CachedAddonObj,
  CachedProductObj,
  ProductObjCacheState,
  SaveProductTitleCachePayload
} from "entities/products/model/store/types"

import { getVariantKeyPrefixLength } from "../variant-key/getVariantKeyPrefixLength"

export function getProductsForType(productType: string): ProductObj[] {
  return Obj.onSite(getCurrentSiteID())
    .where("_objClass", "equals", "Product")
    .and("productType", "equals", productType)
    .toArray() as ProductObj[]
}

export function loadProductsForType(
  productType: string
): Promise<ProductObj[]> {
  return load(() => getProductsForType(productType))
}

export async function loadProductsWithChildrenForType(
  productType: string
): Promise<
  {
    product: ProductObj
    children: ProductBaseVariantObj[]
    addons: Obj<ProductAttributes | ProductAddonAttributes>[]
  }[]
> {
  const products = await load(() => getProductsForType(productType))
  const productsWithChildren = await Promise.all(
    products.map(async product => {
      const children = (await load(() =>
        product.children()
      )) as ProductBaseVariantObj[]
      const addons = (
        await load(() => product.get("addons")).then(addonObjs =>
          Promise.all(
            (addonObjs as ProductAddonWrapperObj[]).map(addon =>
              load(() => addon.get("addon"))
            )
          )
        )
      ).filter(Boolean) as Obj<ProductAttributes | ProductAddonAttributes>[]

      return {
        product,
        children,
        addons
      }
    })
  )

  return productsWithChildren
}

export function getBaseProduct(productObjs: ProductObj[]) {
  return productObjs.find(
    currentProduct =>
      ((currentProduct.get("variantKey")?.split("-") || []) as string[])
        .length === 1
  )
}

export function findProductWithHighestMatch(
  products: ProductObj[],
  attributes: string[]
) {
  const base = getBaseProduct(products)
  return (
    products.reduce(
      ([highestMatchingProduct, prevHighestMatch], currentProduct) => {
        const match = getVariantKeyPrefixLength(
          (currentProduct.get("variantKey")?.split("-") as string[]) || [],
          attributes
        )
        if (match > prevHighestMatch) {
          return [currentProduct, match]
        }
        return [highestMatchingProduct, prevHighestMatch]
      },
      [base || products[0], -Infinity]
    ) as [ProductObj | undefined, number]
  )[0]
}

function buildProductBaseVariantObjCache(
  product: ProductObj,
  presets: ProductBaseVariantObj[],
  addons: Record<string, CachedAddonObj>
): Record<string, CachedProductObj> {
  return presets.reduce(
    (
      cache: Record<string, CachedProductObj>,
      preset: ProductBaseVariantObj
    ) => {
      const variantKey = preset.get("variantKey") as string
      return variantKey
        ? {
            ...cache,
            [variantKey]: {
              id: preset.id(),
              title: preset.get("title"),
              titleShort: preset.get("titleShort"),
              kind: product?.get("kind"),
              variantKey: preset.get("variantKey"),
              hiddenAttributes: product.get("hiddenAttributes"),
              addons
            }
          }
        : cache
    },
    {} as Record<string, CachedProductObj>
  )
}

export function buildProductObjCache(
  productsWithChildren: {
    product: ProductObj
    children: ProductBaseVariantObj[]
    addons: Obj<ProductAttributes | ProductAddonAttributes>[]
  }[]
) {
  if (!productsWithChildren || productsWithChildren.length === 0) return null

  return productsWithChildren.reduce(
    (cache: Record<string, CachedProductObj>, productWithChildren) => {
      const { product, children, addons } = productWithChildren
      const variantKey = product.get("variantKey") as string
      const derivedAddons = addons.reduce(
        (acc, addon) =>
          addon.get("productType")
            ? {
                ...acc,
                [addon.get("productType")]: {
                  title: addon.get("title"),
                  productType: addon.get("productType")
                }
              }
            : acc,
        {} as Record<string, CachedAddonObj>
      )
      return variantKey
        ? {
            ...cache,
            [variantKey]: {
              id: product.id(),
              title: product.get("title"),
              titleShort: product.get("titleShort"),
              kind: product.get("kind"),
              variantKey: product.get("variantKey"),
              hiddenAttributes: product.get("hiddenAttributes"),
              addons: derivedAddons
            },
            ...buildProductBaseVariantObjCache(product, children, derivedAddons)
          }
        : cache
    },
    {} as Record<string, CachedProductObj>
  )
}

export async function loadProductWithHighestMatch({
  variantKey,
  saveCache,
  skipCache
}: {
  variantKey: string
  saveCache?: (payload: SaveProductTitleCachePayload) => void
  skipCache?: boolean
}) {
  const productType = getProductType(variantKey)
  const attributes = getVariantKeyAttributes(variantKey)
  const productsWithChildren = await loadProductsWithChildrenForType(
    productType
  )

  if (saveCache && !skipCache) {
    saveCache({
      productType,
      productTypeCache: buildProductObjCache(productsWithChildren)
    })
  }
  return findProductWithHighestMatch(
    productsWithChildren.map(p => p.product),
    attributes
  )
}

export function loadMatchingProductBaseVariants(
  productObj: ProductObj | null | undefined,
  variantKey: string | null | undefined
): ProductBaseVariantObj[] | null {
  const attributes = getVariantKeyAttributes(variantKey)
  return (
    productObj
      ?.children()
      .map(child => ({
        child: child as ProductBaseVariantObj,
        prefixLength: getVariantKeyPrefixLength(
          getVariantKeyAttributes(child.get("variantKey") as string),
          attributes,
          true
        )
      }))
      .filter(child => child.prefixLength !== -1)
      .sort((a, b) => (a.prefixLength > b.prefixLength ? -1 : 1))
      .map(p => p.child) || null
  )
}

export function getProductObjCacheEntry({
  variantKey,
  cache
}: {
  variantKey: string
  cache: ProductObjCacheState
}) {
  const productType = getProductType(variantKey)
  const productTypeCache = cache[productType]
  if (productTypeCache === null) return null

  const entryKey = productTypeCache
    ? Object.keys(productTypeCache).reduce(
        ({ key, matches }, currentEntryKey) => {
          const currentMatches = getVariantKeyPrefixLength(
            getVariantKeyAttributes(currentEntryKey),
            getVariantKeyAttributes(variantKey),
            true
          )
          if (currentMatches > matches) {
            return { key: currentEntryKey, matches: currentMatches }
          }
          return { key, matches }
        },
        { key: getProductType(variantKey), matches: 1 }
      ).key
    : undefined

  return entryKey ? cache[productType]?.[entryKey] : undefined
}
